import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";

const SITE_URL = "https://navicargologistics.com";
const SOCIAL_IMAGE = `${SITE_URL}/logistics/container-yard.jpg`;

const SERVICE_SEO = {
  "busqueda-productos": {
    title: "Búsqueda de productos en China y Alibaba | NaviCargo",
    description: "Explora Alibaba, 1688 y otras fuentes de China con apoyo para comparar proveedores, revisar condiciones y preparar tu siguiente decisión de compra.",
    keywords: "búsqueda de productos en China, Alibaba México, 1688, sourcing China, proveedores chinos, compras internacionales",
    name: "Búsqueda de productos y proveedores",
  },
  "inspeccion-origen": {
    title: "Inspección de origen antes de embarcar | NaviCargo",
    description: "Verifica calidad, cantidades, empaque y condiciones de tu mercancía antes de que salga del origen.",
    keywords: "inspección de origen, inspección de mercancía, revisión antes de embarque, control de calidad",
    name: "Inspección de origen",
  },
  "inspecciones-china": {
    title: "Inspecciones en China para tu mercancía | NaviCargo",
    description: "Coordina inspecciones en China para revisar proveedores, mercancía, cantidades y empaque antes de tomar decisiones.",
    keywords: "inspecciones en China, inspección de proveedores, mercancía en China, compras en China",
    name: "Inspecciones en China",
  },
  "transporte-internacional": {
    title: "Transporte internacional de carga | NaviCargo",
    description: "Coordina el transporte internacional de tu carga con seguimiento, comunicación clara y una ruta logística de origen a destino.",
    keywords: "transporte internacional, transporte de carga, logística internacional, fletes internacionales",
    name: "Transporte internacional",
  },
  "verificacion-empresas": {
    title: "Verificación de proveedores antes de comprar | NaviCargo",
    description: "Verifica la existencia, capacidad e información comercial de proveedores antes de comprar productos o comprometer un embarque.",
    keywords: "verificación de proveedores, verificar empresas, validar proveedor, comprar productos con seguridad",
    name: "Verificación de proveedores",
  },
  "cierre-negociaciones": {
    title: "Cierre de negociaciones con proveedores | NaviCargo",
    description: "Negocia con proveedores y alinea precio, condiciones, tiempos y próximos pasos antes de comprometer tu compra.",
    keywords: "cierre de negociaciones, negociación con proveedores, negociar compras, acuerdos comerciales",
    name: "Cierre de negociaciones",
  },
  "despacho-aduanero": {
    title: "Despacho aduanero y comercio exterior | NaviCargo",
    description: "Coordina documentación, seguimiento y actores de comercio exterior para que tu mercancía avance por aduana con más orden.",
    keywords: "despacho aduanero, comercio exterior, documentación aduanera, aduana México, logística de importación",
    name: "Despacho aduanero",
  },
  "importacion-exportacion": {
    title: "Importación y exportación de mercancías | NaviCargo",
    description: "Coordina tus operaciones de importación y exportación con acompañamiento logístico, claridad documental y control de cada etapa.",
    keywords: "importación y exportación, comercio exterior, logística aduanal, importar mercancía",
    name: "Importación y exportación",
  },
  "logistica-integral": {
    title: "Logística integral para empresas | NaviCargo",
    description: "Conecta proveedores, inspecciones, transporte y almacenamiento en una operación logística integral y coordinada.",
    keywords: "logística integral, operador logístico, coordinación logística, cadena de suministro",
    name: "Logística integral",
  },
  "renta-bodegas": {
    title: "Renta de bodegas y almacenamiento | NaviCargo",
    description: "Encuentra espacios de almacenamiento para tu mercancía con opciones de bodega y coordinación logística.",
    keywords: "renta de bodegas, almacenamiento de mercancía, bodegas logísticas, almacenaje",
    name: "Renta de bodegas",
  },
};

const PAGE_SEO = {
  "/": {
    title: "NaviCargo | Logística internacional para importar y exportar",
    description: "NaviCargo conecta proveedores, productos y mercados. Encuentra, verifica, inspecciona, transporta y almacena tu carga con claridad.",
    keywords: "NaviCargo, logística internacional, importar y exportar, proveedores, inspección de mercancía",
  },
  "/about": {
    title: "Sobre NaviCargo | Tu socio logístico internacional",
    description: "Conoce NaviCargo, el socio logístico que ayuda a empresas a encontrar, verificar y mover productos entre mercados con mayor control.",
    keywords: "sobre NaviCargo, empresa logística, socio logístico, comercio internacional",
  },
  "/services": {
    title: "Servicios logísticos para empresas | NaviCargo",
    description: "Descubre los servicios de NaviCargo: búsqueda de productos, verificación de proveedores, inspecciones, transporte, importación, exportación y bodegas.",
    keywords: "servicios logísticos, logística para empresas, inspección, transporte, bodegas",
  },
  "/contact": {
    title: "Contacto | NaviCargo Logistics",
    description: "Habla con NaviCargo por WhatsApp, teléfono o correo y recibe orientación para tu próxima operación logística.",
    keywords: "contacto NaviCargo, cotización logística, asesoría logística, WhatsApp",
  },
};

const PAGE_SEO_EN = {
  "/": {
    title: "NaviCargo | International logistics for import and export",
    description: "NaviCargo connects suppliers, products and markets. Find, verify, inspect, transport and store your cargo with clarity.",
    keywords: "NaviCargo, international logistics, import and export, suppliers, cargo inspection",
  },
  "/about": {
    title: "About NaviCargo | Your international logistics partner",
    description: "Meet NaviCargo, the logistics partner helping companies find, verify and move products between markets with greater control.",
    keywords: "about NaviCargo, logistics company, logistics partner, international trade",
  },
  "/services": {
    title: "Logistics services for businesses | NaviCargo",
    description: "Discover NaviCargo services: product sourcing, supplier verification, inspections, transportation, import, export and warehousing.",
    keywords: "logistics services, business logistics, inspection, transportation, warehousing",
  },
  "/contact": {
    title: "Contact | NaviCargo Logistics",
    description: "Talk to NaviCargo on WhatsApp, by phone or email and get guidance for your next logistics operation.",
    keywords: "contact NaviCargo, logistics quote, logistics advice, WhatsApp",
  },
};

const SERVICE_SEO_EN = {
  "busqueda-productos": ["Product sourcing in China and Alibaba | NaviCargo", "Explore Alibaba, 1688 and other China sources with support to compare suppliers, review terms and prepare your next buying decision.", "product sourcing China, Alibaba sourcing, 1688, Chinese suppliers, international purchasing"],
  "inspeccion-origen": ["Origin inspection before shipping | NaviCargo", "Verify cargo quality, quantities, packaging and conditions before it leaves origin.", "origin inspection, cargo inspection, pre-shipment review, quality control"],
  "inspecciones-china": ["Inspections in China for your cargo | NaviCargo", "Coordinate inspections in China to review suppliers, cargo, quantities and packaging before making decisions.", "inspections in China, supplier inspection, cargo in China, China sourcing"],
  "transporte-internacional": ["International cargo transportation | NaviCargo", "Coordinate international transportation with follow-up, clear communication and a route from origin to destination.", "international transportation, cargo freight, global logistics, international shipping"],
  "verificacion-empresas": ["Supplier verification before buying | NaviCargo", "Verify supplier existence, capacity and commercial information before buying or committing a shipment.", "supplier verification, verify a company, validate supplier, safe purchasing"],
  "cierre-negociaciones": ["Supplier negotiation closing | NaviCargo", "Negotiate with suppliers and align price, terms, timelines and next steps before committing your purchase.", "negotiation closing, supplier negotiation, buying negotiations, commercial agreements"],
  "despacho-aduanero": ["Customs clearance and foreign trade | NaviCargo", "Coordinate documentation, follow-up and foreign trade stakeholders so your cargo moves through customs with more order.", "customs clearance, foreign trade, customs documentation, import logistics"],
  "importacion-exportacion": ["Import and export operations | NaviCargo", "Coordinate import and export operations with logistics support, document clarity and control at every stage.", "import and export, foreign trade, customs logistics, importing cargo"],
  "logistica-integral": ["End-to-end logistics for businesses | NaviCargo", "Connect suppliers, inspections, transportation and storage in one coordinated logistics operation.", "end-to-end logistics, logistics operator, logistics coordination, supply chain"],
  "renta-bodegas": ["Warehouse rental and storage | NaviCargo", "Find storage space for your cargo with warehouse options and logistics coordination.", "warehouse rental, cargo storage, logistics warehouses, warehousing"],
};

const upsertMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const upsertLink = (rel, href, extraAttributes = {}) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
  Object.entries(extraAttributes).forEach(([key, value]) => element.setAttribute(key, value));
};

const upsertStructuredData = (canonicalUrl, seo, service) => {
  const organization = {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "NaviCargo Logistics",
    alternateName: "NaviCargo",
    url: SITE_URL,
    logo: `${SITE_URL}/navicargo-logo-white-transparent.png`,
    image: SOCIAL_IMAGE,
    email: "contacto@navicargologistics.com",
    telephone: "+52 33 2258 4766",
    areaServed: "MX",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+52 33 2258 4766",
      contactType: "sales",
      availableLanguage: ["Spanish", "English"],
    },
  };

  const graph = [
    organization,
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: "NaviCargo Logistics",
      alternateName: "NaviCargo",
      url: SITE_URL,
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: "es-MX",
    },
    {
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: seo.title,
      description: seo.description,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
      inLanguage: "es-MX",
    },
  ];

  if (service) {
    graph.push({
      "@type": "Service",
      "@id": `${canonicalUrl}#service`,
      name: service.name,
      description: seo.description,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: "MX",
      serviceType: service.name,
    });
  }

  let script = document.head.querySelector('script[data-seo="structured-data"]');
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seo = "structured-data";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
};

export default function Seo() {
  const { pathname } = useLocation();
  const { isEnglish } = useLanguage();

  useEffect(() => {
    const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
    const serviceSlug = normalizedPath.startsWith("/services/") ? normalizedPath.split("/").pop() : null;
    const service = serviceSlug
      ? isEnglish
        ? (() => {
            const [title, description, keywords] = SERVICE_SEO_EN[serviceSlug] || [];
            const fallback = SERVICE_SEO[serviceSlug];
            return title ? { title, description, keywords, name: fallback.name } : fallback;
          })()
        : SERVICE_SEO[serviceSlug]
      : null;
    const seo = service || (isEnglish ? PAGE_SEO_EN[normalizedPath] || PAGE_SEO_EN["/"] : PAGE_SEO[normalizedPath] || PAGE_SEO["/"]);
    const canonicalUrl = `${SITE_URL}${normalizedPath === "/" ? "" : normalizedPath}`;

    document.documentElement.lang = isEnglish ? "en" : "es-MX";
    document.title = seo.title;

    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertMeta("name", "author", "NaviCargo Logistics");
    upsertMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    upsertMeta("name", "googlebot", "index, follow");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "NaviCargo Logistics");
    upsertMeta("property", "og:locale", isEnglish ? "en_US" : "es_MX");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", SOCIAL_IMAGE);
    upsertMeta("property", "og:image:alt", "Operación logística internacional de NaviCargo");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", SOCIAL_IMAGE);
    upsertLink("canonical", canonicalUrl);
     upsertLink("alternate", canonicalUrl, { hreflang: isEnglish ? "en" : "es-MX" });

    upsertStructuredData(canonicalUrl, seo, service);
  }, [pathname, isEnglish]);

  return null;
}